import { useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSearchParams } from "react-router-dom";
import {
  getClaimsAdminLoginUrl,
  normalizeClaimsAdminPortal,
} from "../../utils/claimsPortal";
import { clearClientSession } from "../../services/session";
import logo from "../../assets/images/logo.svg";
import claimsHero from "../../assets/images/admin-bg-claims.jpg";
import ifasHero from "../../assets/images/admin-bg-ifas.jpg";

// Logged-out counterpart of the Claims/Funerals admin login
// (src/features/admin-login). Same split layout, brand hero, logo and
// footer as the login screen, in a signed-out state. Reproduces the
// approved mock: .claude/agent-team/mocks/approved/claims-logout-mock.html
const portalContent = {
  claims: {
    eyebrow: "Claims Admin",
    portalName: "Claims",
    image: claimsHero,
  },
  ifas: {
    eyebrow: "Funerals Admin",
    portalName: "Funerals",
    image: ifasHero,
  },
};

export default function LoggedOutPage() {
  const [searchParams] = useSearchParams();

  const portal = useMemo(() => {
    const fromQuery = searchParams.get("portal");
    if (fromQuery) return normalizeClaimsAdminPortal(fromQuery);
    let stored: string | null = null;
    try {
      stored = localStorage.getItem("selectedPortal");
    } catch {
      stored = null;
    }
    return normalizeClaimsAdminPortal(stored);
  }, [searchParams]);

  const content = portalContent[portal];

  // Landing here means the session is over, whichever path got us here
  // (manual logout via the Cognito hosted logout, a 401, or a route guard).
  // Clearing on arrival keeps the token teardown off the logout click, so
  // Amplify still has what it needs to end the Cognito session.
  useEffect(() => {
    clearClientSession();
  }, []);

  const handleBackToLogin = () => {
    // Canonical admin login for this portal keeps the Cognito callback
    // URLs consistent even if we are on a non-canonical origin.
    window.location.assign(getClaimsAdminLoginUrl(portal));
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "#FFFFFF" }}>
      {/* Left: brand photo (hidden on small screens) — same asset as login */}
      <Box
        aria-hidden
        sx={{
          display: { xs: "none", md: "block" },
          flexBasis: "50%",
          position: "relative",
          backgroundImage: `url(${content.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.30))",
          }}
        />
      </Box>

      {/* Right: signed-out column */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 3, md: 6 },
            py: 6,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Icebolethu Group"
            sx={{
              height: { xs: 52, md: 60 },
              width: "auto",
              maxWidth: 230,
              objectFit: "contain",
              mb: 3.5,
            }}
          />

          <Box
            aria-hidden
            sx={{
              width: 52,
              height: 52,
              borderRadius: "999px",
              display: "grid",
              placeItems: "center",
              bgcolor: "#FDECD9",
              mb: 2.5,
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 28, color: "#EC6E00" }} />
          </Box>

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "1.4px",
              textTransform: "uppercase",
              color: "#5F6676",
              mb: 1.5,
            }}
          >
            {content.eyebrow}
          </Typography>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 24, md: 28 },
              lineHeight: 1.2,
              fontWeight: 800,
              color: "#1A1F2E",
            }}
          >
            You&#39;ve been signed out
          </Typography>

          <Typography
            sx={{
              color: "#5F6676",
              fontSize: 14,
              lineHeight: 1.6,
              mt: 1.5,
              maxWidth: 340,
            }}
          >
            Your session has ended and you&#39;ve been securely signed out of the{" "}
            {content.portalName} administration portal. Sign in again to continue.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleBackToLogin}
            sx={{
              mt: 3,
              minWidth: 220,
              bgcolor: "#EC6E00",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              py: 1.25,
              boxShadow: "none",
              "&:hover": { bgcolor: "#D86300", boxShadow: "none" },
            }}
          >
            Back to login
          </Button>

          <Box
            sx={{
              mt: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              color: "#5F6676",
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
            Signed out securely
          </Box>
        </Box>

        <Box
          component="footer"
          sx={{
            width: "100%",
            borderTop: "1px solid rgba(15,23,42,0.08)",
            px: { xs: 3, md: 6 },
            pt: 2,
            pb: 4,
            textAlign: "center",
            color: "#5F6676",
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          <Box>
            <Box component="span" sx={{ fontWeight: 700, color: "#1A1F2E" }}>
              Call Centre:
            </Box>{" "}
            086 000 7580
          </Box>
          <Box>
            <Box component="span" sx={{ fontWeight: 700, color: "#1A1F2E" }}>
              Email:
            </Box>{" "}
            info@icebolethu.co.za
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
